import { useMutation, useQuery } from '@tanstack/react-query';
import { apiClient } from './client';
import type {
  TradingDecisionEvidenceDeleteResponse,
  TradingDecisionEvidenceResponse,
  TradingDecisionNodeResponse,
  TradingDecisionTraceResponse,
} from '@/types/api';

export interface TradingDecisionTraceParams {
  station: string;
  startDate: string;
  endDate: string;
}

export interface DecisionNodePayload {
  station: string;
  record_date: string;
  target_date: string;
  node_code: string;
  evidence_interpretation?: string;
  market_judgement?: string;
  action_taken?: string;
  risk_note?: string;
  result_review?: string;
  self_score?: number | null;
}

export interface DecisionNodeKeyPayload {
  station: string;
  record_date: string;
  target_date: string;
  node_code: string;
}

export interface EvidenceTextPayload extends DecisionNodeKeyPayload {
  evidence_type: 'chat_text' | 'manual_note';
  title: string;
  content_text: string;
  tags: string[];
}

export interface EvidenceImagePayload extends DecisionNodeKeyPayload {
  title: string;
  tags: string[];
  file: File;
}

export function useTradingDecisionTrace(params: TradingDecisionTraceParams) {
  return useQuery({
    queryKey: ['trading-decision-trace', params.station, params.startDate, params.endDate],
    queryFn: async () => {
      const res = await apiClient.get<TradingDecisionTraceResponse>('/trading-diary/decision-trace', {
        params: {
          station: params.station,
          start_date: params.startDate,
          end_date: params.endDate,
        },
      });
      return res.data;
    },
    enabled: Boolean(params.station && params.startDate && params.endDate),
    staleTime: 5 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
  });
}

export async function saveDecisionNode(params: DecisionNodePayload) {
  const { station, ...payload } = params;
  const res = await apiClient.put<TradingDecisionNodeResponse>('/trading-diary/decision-node', payload, {
    params: { station },
  });
  return res.data;
}

export async function archiveDecisionNode(params: DecisionNodeKeyPayload) {
  const { station, ...payload } = params;
  const res = await apiClient.post<TradingDecisionNodeResponse>('/trading-diary/decision-node/archive', payload, {
    params: { station },
  });
  return res.data;
}

export async function addTextEvidence(params: EvidenceTextPayload) {
  const { station, ...payload } = params;
  const res = await apiClient.post<TradingDecisionEvidenceResponse>('/trading-diary/decision-node/evidence/text', payload, {
    params: { station },
  });
  return res.data;
}

export async function uploadEvidenceImage(params: EvidenceImagePayload) {
  const { station, record_date, target_date, node_code, title, tags, file } = params;
  const formData = new FormData();
  formData.append('record_date', record_date);
  formData.append('target_date', target_date);
  formData.append('node_code', node_code);
  formData.append('title', title);
  formData.append('tags', tags.join(','));
  formData.append('file', file);
  const res = await apiClient.post<TradingDecisionEvidenceResponse>('/trading-diary/decision-node/evidence', formData, {
    params: { station },
  });
  return res.data;
}

export async function deleteEvidence(params: { station: string; evidenceId: number }) {
  const res = await apiClient.delete<TradingDecisionEvidenceDeleteResponse>(
    `/trading-diary/decision-node/evidence/${params.evidenceId}`,
    { params: { station: params.station } }
  );
  return res.data;
}

export function useSaveDecisionNode() {
  return useMutation({
    mutationFn: saveDecisionNode,
  });
}

export function useArchiveDecisionNode() {
  return useMutation({
    mutationFn: archiveDecisionNode,
  });
}

export function useAddTextEvidence() {
  return useMutation({
    mutationFn: addTextEvidence,
  });
}

export function useUploadEvidenceImage() {
  return useMutation({
    mutationFn: uploadEvidenceImage,
  });
}

export function useDeleteEvidence() {
  return useMutation({
    mutationFn: deleteEvidence,
  });
}
